import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Brush,
  Cell,
  LabelList,
} from 'recharts';
import Spinner from '../../../../../components/Spinner';
import useFetch from '../../../../../hooks/useFetch';
import DashboardService from '../../../../../services/dashboard.service';
import { formatNumber } from '../../../../../utils/methods';
import { MonthYearValue } from './MonthYearFilter';
import { ViewMode } from './ViewModeToggle';

interface DivisionSecondaryBrushProps {
  period: MonthYearValue;
  viewMode: ViewMode;
}

interface DivisionRow {
  division_id: number;
  division_name: string;
  issued_qty: number;
  issued_qty_value: number;
}

const PALETTE = [
  '#696cff',
  '#fdb528',
  '#71dd37',
  '#03c3ec',
  '#ff3e1d',
  '#8592a3',
  '#14b8a6',
  '#f59e0b',
  '#8b5cf6',
  '#ef4444',
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="card shadow-sm p-3" style={{ minWidth: 180 }}>
        <p className="fw-semibold mb-1 text-heading" style={{ fontSize: 13 }}>
          {label}
        </p>
        <p className="mb-0 small" style={{ color: payload[0]?.fill }}>
          <strong>{formatNumber(payload[0]?.value)}</strong>
        </p>
      </div>
    );
  }
  return null;
};

const DivisionSecondaryBrush: React.FC<DivisionSecondaryBrushProps> = ({ period, viewMode }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<DivisionRow[]>([]);

  const { loading } = useFetch({
    service: DashboardService,
    func: 'divisionSecondary',
    payload: { month: period.month, year: period.year },
    dependency: [period.month, period.year],
    onSuccess: (response: DivisionRow[]) => {
      setRows(response || []);
    },
  });

  const getValue = (r: DivisionRow) =>
    viewMode === 'value' ? r.issued_qty_value : r.issued_qty;

  const chartData = [...rows]
    .sort((a, b) => getValue(b) - getValue(a))
    .map((r, index) => ({
      name: r.division_name,
      value: getValue(r),
      fill: PALETTE[index % PALETTE.length],
    }));

  // Show a brush window of up to 6 items, or all if fewer
  const brushEnd = Math.min(chartData.length - 1, 5);

  return (
    <div className="col-12">
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <div>
            <h5 className="card-title mb-0">
              {t('dashboard.pageTexts.divisionSecondaryBrush.title')}
            </h5>
            <p className="text-muted small mb-0 mt-1">
              {t('dashboard.pageTexts.divisionSecondaryBrush.subtitle')}
            </p>
          </div>
          <span className="badge bg-label-primary">
            <i className="ri-bar-chart-2-line me-1" />
            {t('dashboard.pageTexts.divisionSecondaryBrush.brushHint')}
          </span>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 320 }}>
              <Spinner variant="primary" />
            </div>
          ) : chartData.length === 0 ? (
            <div className="d-flex justify-content-center align-items-center text-muted" style={{ minHeight: 320 }}>
              <span>{t('dashboard.pageTexts.divisionSecondaryBrush.noData')}</span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={360}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 24, left: 8, bottom: 60 }}
                barCategoryGap="30%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  angle={-35}
                  textAnchor="end"
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => formatNumber(v)}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Brush
                  dataKey="name"
                  height={28}
                  stroke="#696cff"
                  fill="rgba(105,108,255,0.06)"
                  travellerWidth={8}
                  startIndex={0}
                  endIndex={brushEnd}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={40}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                  <LabelList
                    dataKey="value"
                    position="top"
                    formatter={(v: any) => formatNumber(v as number)}
                    style={{ fontSize: 10, fill: '#6c757d' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default DivisionSecondaryBrush;
